import { useSelector, useDispatch } from 'react-redux';
import { useEffect, useCallback, useState } from 'react';
import { UsersState } from '../../store/reducers/usersReducer';
import { setUserProfile, setProfileLoading, clearUserProfile } from '../../store/actions/usersActions';
import getProfile, { clearProfileCache } from '../../services/Users.Service';
import UserEntity from '../../entities/User.Entity';

interface UseUserProfileReturn {
    userProfile: UserEntity | null;
    isLoading: boolean;
    isLoaded: boolean;
    refreshProfile: () => Promise<void>;
    clearProfile: () => void;
}

export const useUserProfile = (forceRefresh: boolean = false): UseUserProfileReturn => {
    const dispatch = useDispatch();
    const { userProfile, isProfileLoaded, profileLoading, lastProfileFetch } =
        useSelector<UsersState, UsersState>((state) => state);

    const [authToken, setAuthToken] = useState<string | null>(
        window.sessionStorage.getItem('Authorization')
    );

    // ⚠️ Minimal change: track if profile request has failed permanently
    const [profileError, setProfileError] = useState(false);

    const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

    // ---------------------------------------
    // Monitor token changes and reset profile
    // ---------------------------------------
    useEffect(() => {
        const checkToken = () => {
            const token = window.sessionStorage.getItem('Authorization');

            // Token changed → clear profile
            if (authToken && token && authToken !== token) {
                dispatch(clearUserProfile());
                clearProfileCache();
                setProfileError(false); // reset error flag
                setAuthToken(token);
            }
            // Token removed → logout
            else if (authToken && !token) {
                dispatch(clearUserProfile());
                clearProfileCache();
                setProfileError(false);
                setAuthToken(null);
            }
            // Token added (login)
            else if (!authToken && token) {
                setAuthToken(token);
            }
        };

        // Check immediately
        checkToken();

         // Monitor token changes continuously (every 500ms)
        const interval = setInterval(checkToken, 500);
        return () => clearInterval(interval);
    }, [authToken, dispatch]);

    // ---------------------------------------
    // Check if profile should be fetched
    // ---------------------------------------
    const shouldFetchProfile = useCallback(() => {
        // 🚫 NEW: do not retry if a fatal error occurred
        if (profileError) return false;

        if (forceRefresh) return true;
        if (!isProfileLoaded) return true;
        if (profileLoading) return false;

        // Check if cache has expired
        if (lastProfileFetch && (Date.now() - lastProfileFetch) > CACHE_DURATION) {
            return true;
        }

        return false;
    }, [forceRefresh, isProfileLoaded, profileLoading, lastProfileFetch, profileError]);

    // ---------------------------------------
    // Fetch profile safely
    // ---------------------------------------
    const fetchProfile = useCallback(async () => {
        // Do not attempt fetch if there is no token or a fatal error occurred
        if (!authToken || profileLoading || profileError) return;

        dispatch(setProfileLoading(true));

        try {
            const abortController = new AbortController();
            const profile = await getProfile(abortController.signal);
            dispatch(setUserProfile(profile));

            // If successful, ensure error flag is cleared
            setProfileError(false);
        } catch (error) {
            console.error('Error fetching user profile:', error);

            // NEW: avoid infinite loop
            setProfileError(true);

            // ✅ NEW: mark profile as "loaded" so UI can render normally
            dispatch(setUserProfile(null)); 

            dispatch(setProfileLoading(false))
        }
    }, [dispatch, profileLoading, authToken, profileError]);

    const refreshProfile = useCallback(async () => {
        await fetchProfile();
    }, [fetchProfile]);

    const clearProfile = useCallback(() => {
        dispatch(clearUserProfile());
        setProfileError(false); // also clear error state
    }, [dispatch]);

    // ---------------------------------------
    // Auto-fetch profile when needed
    // ---------------------------------------
    useEffect(() => {
        // ✅ Only run if there's a token and we need to fetch the profile
        if (authToken && shouldFetchProfile()) {
            fetchProfile();
        }
    }, [authToken, shouldFetchProfile, fetchProfile]);

    return {
        userProfile,
        isLoading: profileLoading,
        isLoaded: isProfileLoaded,
        refreshProfile,
        clearProfile
    };
};